// Admin > Audit Logs > Messages: every WhatsApp, SMS and email the app sent or tried to send —
// to whom, which template, what it said (secrets hidden), the result and the provider's reply.
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box, Typography, Paper, Stack, TextField, MenuItem, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  TablePagination, Drawer, IconButton, Divider, Alert, LinearProgress, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import { MESSAGE_LOGS_QUERY } from '../graphql/queries';

const CHANNEL = {
  whatsapp: { label: 'WhatsApp', icon: <WhatsAppIcon fontSize="small" sx={{ color: '#25D366' }} /> },
  sms: { label: 'SMS', icon: <SmsIcon fontSize="small" color="primary" /> },
  email: { label: 'Email', icon: <EmailIcon fontSize="small" color="secondary" /> },
};
const STATUS = {
  sent: ['Sent', 'success'], failed: ['Failed', 'error'], held: ['Held (sending off)', 'warning'],
  manual: ['Manual WhatsApp link', 'info'], skipped: ['Skipped', 'default'],
};
const PURPOSE = {
  vendor_invite: 'Vendor invitation', vendor_credentials: 'Vendor login details', survey_invite: 'Survey', invitation: 'Wedding invitation',
  notification: 'Notification', password_reset: 'Password reset', direct: 'Sent by an admin', test: 'Test', email: 'Email', secondary_copy: 'Copy to secondary contact',
};
const EMPTY = { channel: '', status: '', purpose: '', from: '', to: '', search: '' };
const fmt = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
const isHtml = (s) => /<\w+[^>]*>/.test(s || '');

function toCsv(rows) {
  const cols = ['createdAt', 'channel', 'status', 'purpose', 'recipient', 'recipientEmail', 'templateName', 'subject', 'error', 'provider', 'providerMessageId', 'sentBy'];
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [cols.join(','), ...rows.map(r => cols.map(c => cell(r[c])).join(','))].join('\n');
}

export default function MessageLogsPanel() {
  const [filter, setFilter] = useState(EMPTY);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(null);
  const vars = {
    filter: Object.fromEntries(Object.entries(filter).filter(([, v]) => v !== '').map(([k, v]) => [k, k === 'search' ? v.trim() : v])),
    limit: rowsPerPage, offset: page * rowsPerPage,
  };
  const { data, loading, error, refetch } = useQuery(MESSAGE_LOGS_QUERY, { variables: vars, fetchPolicy: 'cache-and-network' });
  const rows = data?.messageLogs?.rows || [];
  const total = data?.messageLogs?.total || 0;
  const counts = data?.messageLogs?.counts || [];
  const set = (k) => (e) => { setFilter(f => ({ ...f, [k]: e.target.value })); setPage(0); };
  const sum = (pred) => counts.filter(pred).reduce((n, c) => n + c.count, 0);

  const exportCsv = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }));
    a.download = `message-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} mb={2}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {Object.entries(CHANNEL).map(([k, c]) => (
            <Chip key={k} icon={c.icon} variant={filter.channel === k ? 'filled' : 'outlined'} onClick={() => { setFilter(f => ({ ...f, channel: f.channel === k ? '' : k })); setPage(0); }}
              label={`${c.label}: ${sum(x => x.channel === k)} (${sum(x => x.channel === k && x.status === 'failed')} failed)`} />
          ))}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => refetch()}>Refresh</Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} disabled={!rows.length} onClick={exportCsv}>Export page (CSV)</Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField size="small" placeholder="Search recipient, subject, template, error…" value={filter.search} onChange={set('search')} sx={{ flex: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <TextField size="small" type="date" label="From" value={filter.from} onChange={set('from')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="To" value={filter.to} onChange={set('to')} InputLabelProps={{ shrink: true }} />
          <TextField size="small" select label="Channel" value={filter.channel} onChange={set('channel')} sx={{ minWidth: 120 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(CHANNEL).map(([k, c]) => <MenuItem key={k} value={k}>{c.label}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Result" value={filter.status} onChange={set('status')} sx={{ minWidth: 140 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(STATUS).map(([k, [l]]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="For" value={filter.purpose} onChange={set('purpose')} sx={{ minWidth: 170 }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(PURPOSE).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
          </TextField>
          <Button startIcon={<FilterAltOffIcon />} onClick={() => { setFilter(EMPTY); setPage(0); }}>Clear</Button>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>}
      <Paper variant="outlined">
        {loading && <LinearProgress />}
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.100' } }}>
                <TableCell>When</TableCell><TableCell>Channel</TableCell><TableCell>To</TableCell><TableCell>For</TableCell>
                <TableCell>Template / subject</TableCell><TableCell>Result</TableCell><TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !rows.length && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No messages match.</TableCell></TableRow>}
              {rows.map(r => {
                const [label, color] = STATUS[r.status] || [r.status, 'default'];
                return (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="caption">{fmt(r.createdAt)}</Typography></TableCell>
                    <TableCell><Stack direction="row" spacing={0.5} alignItems="center">{CHANNEL[r.channel]?.icon}<Typography variant="body2">{CHANNEL[r.channel]?.label || r.channel}</Typography></Stack></TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.recipient || '—'}</Typography>
                      {r.recipientEmail && r.recipientEmail !== r.recipient && <Typography variant="caption" color="text.secondary">{r.recipientEmail}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="body2">{PURPOSE[r.purpose] || r.purpose || '—'}</Typography></TableCell>
                    <TableCell sx={{ maxWidth: 300 }}><Typography variant="body2" noWrap>{r.subject || r.templateName || '—'}</Typography></TableCell>
                    <TableCell>
                      <Chip size="small" label={label} color={color} variant={r.status === 'sent' ? 'filled' : 'outlined'} />
                      {r.error && <Typography variant="caption" color={r.status === 'failed' ? 'error' : 'text.secondary'} display="block" noWrap sx={{ maxWidth: 240 }}>{r.error}</Typography>}
                    </TableCell>
                    <TableCell><Typography variant="caption">{r.sentBy || 'System'}</Typography></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
        <TablePagination component="div" count={total} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[25, 50, 100, 200]} />
      </Paper>

      <Drawer anchor="right" open={Boolean(open)} onClose={() => setOpen(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2.5 } }}>
        {open && (
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">{CHANNEL[open.channel]?.icon}<Typography variant="h6" fontWeight={700}>{CHANNEL[open.channel]?.label} message</Typography></Stack>
              <IconButton onClick={() => setOpen(null)}><CloseIcon /></IconButton>
            </Stack>
            <Typography variant="body2" color="text.secondary" mb={1}>{fmt(open.createdAt)}</Typography>
            <Stack direction="row" spacing={1} mb={1}>
              <Chip size="small" label={(STATUS[open.status] || [open.status])[0]} color={(STATUS[open.status] || [])[1] || 'default'} />
              {open.purpose && <Chip size="small" variant="outlined" label={PURPOSE[open.purpose] || open.purpose} />}
            </Stack>
            {[
              ['To', open.recipient], ['Account', open.recipientEmail], ['Template', open.templateName], ['Subject', open.subject],
              ['Provider', open.provider], ['Provider message id', open.providerMessageId], ['Sent by', open.sentBy || 'System'],
              ['On behalf of', open.actingFor], ['Related record', open.relatedType ? `${open.relatedType} ${open.relatedId || ''}` : null],
            ].filter(([, v]) => v).map(([k, v]) => (
              <Typography key={k} variant="body2" sx={{ mb: 0.5, wordBreak: 'break-word' }}><Box component="span" color="text.secondary">{k}: </Box>{v}</Typography>
            ))}
            {open.error && <Alert severity={open.status === 'failed' ? 'error' : 'info'} sx={{ my: 1 }}>{open.error}</Alert>}
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={700} mb={1}>Message</Typography>
            {!open.body && <Typography variant="body2" color="text.secondary">Not recorded.</Typography>}
            {open.body && open.status === 'manual' && /^https:\/\/wa\.me\//.test(open.body) && (
              <Button size="small" href={open.body} target="_blank" rel="noopener noreferrer" sx={{ mb: 1 }}>Open in WhatsApp</Button>
            )}
            {open.body && (isHtml(open.body)
              ? <Paper variant="outlined"><iframe title="Message" sandbox="" srcDoc={open.body} style={{ width: '100%', height: 420, border: 0 }} /></Paper>
              : <Box component="pre" sx={{ bgcolor: '#f7f7f9', p: 1.5, borderRadius: 1, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>{open.body}</Box>)}
          </Box>
        )}
      </Drawer>
    </Box>
  );
}
